/**
 * usePersistedFilters — React hook for auto-restoring filter state and managing
 * saved presets via localStorage.
 *
 * Usage:
 *   const { filters, setFilters, clearFilters, hasActiveFilters,
 *           presets, savePreset, applyPreset, deletePreset }
 *     = usePersistedFilters<MyFilterState>({
 *         scopeKey: `docs.${projectId}`,
 *         defaultState: { searchQuery: '', statusFilter: '', sortPreset: 'updated-desc' },
 *       });
 */

import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
  loadFilterStore,
  saveFilterStore,
  type FilterStore,
  type SavedFilterPreset,
} from '../utils/filterStorage';

// ── Options ──────────────────────────────────────────────────────────────────

interface UsePersistedFiltersOptions<T extends Record<string, unknown>> {
  /** Unique key scoping this filter set (e.g. "docs.proj_abc", "board.proj.board") */
  scopeKey: string;
  /** The default (empty) filter state */
  defaultState: T;
  /** Optional sanitizer — strips stale/invalid values after restore */
  sanitize?: (state: T) => T;
  /** Debounce delay in ms for persisting to localStorage (default 500) */
  persistDelay?: number;
  /** Disable persistence entirely (still provides in-memory state) */
  enabled?: boolean;
}

// ── Return type ──────────────────────────────────────────────────────────────

interface UsePersistedFiltersReturn<T extends Record<string, unknown>> {
  filters: T;
  setFilters: (partial: Partial<T>) => void;
  replaceFilters: (full: T) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  presets: SavedFilterPreset[];
  savePreset: (name: string) => void;
  applyPreset: (id: string) => void;
  deletePreset: (id: string) => void;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function usePersistedFilters<T extends Record<string, unknown>>({
  scopeKey,
  defaultState,
  sanitize,
  persistDelay = 500,
  enabled = true,
}: UsePersistedFiltersOptions<T>): UsePersistedFiltersReturn<T> {
  // ── Helper: read saved state for a given scope key ─────────────────────
  const readSavedState = useCallback(
    (key: string): T => {
      if (!enabled) return defaultState;
      try {
        const store = loadFilterStore();
        const saved = store.lastFilters[key];
        if (saved) {
          const restored = { ...defaultState, ...saved.state } as T;
          return sanitize ? sanitize(restored) : restored;
        }
      } catch {
        // fall through
      }
      return defaultState;
    },
    // defaultState and sanitize are stable per call-site — safe to omit from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enabled]
  );

  // ── Initialise from localStorage ────────────────────────────────────────
  const [filters, setFiltersRaw] = useState<T>(() => readSavedState(scopeKey));

  // ── Presets (kept in sync with localStorage) ────────────────────────────
  const [presets, setPresets] = useState<SavedFilterPreset[]>(() => {
    if (!enabled) return [];
    try {
      return loadFilterStore().presets[scopeKey] || [];
    } catch {
      return [];
    }
  });

  // ── Debounced persistence ───────────────────────────────────────────────
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // ── Re-sync when scopeKey changes (e.g. project switch) ──────────────
  const prevScopeKeyRef = useRef(scopeKey);
  useEffect(() => {
    if (prevScopeKeyRef.current === scopeKey) return;
    prevScopeKeyRef.current = scopeKey;

    // Flush any pending writes for the OLD scope before switching
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // Load state for the NEW scope (or defaults if nothing saved)
    setFiltersRaw(readSavedState(scopeKey));

    // Load presets for the new scope
    try {
      const store = loadFilterStore();
      setPresets(store.presets[scopeKey] || []);
    } catch {
      setPresets([]);
    }
  }, [scopeKey, readSavedState]);

  const persistNow = useCallback(() => {
    if (!enabled) return;
    const store = loadFilterStore();
    store.lastFilters[scopeKey] = {
      state: filtersRef.current as Record<string, unknown>,
      savedAt: Date.now(),
    };
    saveFilterStore(store);
  }, [scopeKey, enabled]);

  const schedulePersist = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(persistNow, persistDelay);
  }, [persistNow, persistDelay]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // ── Mutators ────────────────────────────────────────────────────────────

  const setFilters = useCallback(
    (partial: Partial<T>) => {
      setFiltersRaw((prev) => {
        const next = { ...prev, ...partial };
        return next;
      });
      schedulePersist();
    },
    [schedulePersist]
  );

  const replaceFilters = useCallback(
    (full: T) => {
      setFiltersRaw(full);
      schedulePersist();
    },
    [schedulePersist]
  );

  const clearFilters = useCallback(() => {
    setFiltersRaw(defaultState);
    if (enabled) {
      // Remove from store immediately
      const store = loadFilterStore();
      delete store.lastFilters[scopeKey];
      saveFilterStore(store);
    }
  }, [defaultState, scopeKey, enabled]);

  // ── hasActiveFilters ────────────────────────────────────────────────────

  const hasActiveFilters = useMemo(() => {
    for (const key of Object.keys(defaultState)) {
      const def = defaultState[key];
      const cur = filters[key];
      if (Array.isArray(def)) {
        if ((cur as unknown[])?.length > 0) return true;
      } else if (typeof def === 'boolean') {
        if (cur !== def) return true;
      } else if (typeof def === 'string') {
        if (cur && cur !== def) return true;
      } else if (def === undefined || def === null) {
        if (cur !== def) return true;
      }
    }
    return false;
  }, [filters, defaultState]);

  // ── Preset management ──────────────────────────────────────────────────

  const persistPresets = useCallback(
    (next: SavedFilterPreset[]) => {
      setPresets(next);
      if (!enabled) return;
      const store = loadFilterStore();
      store.presets[scopeKey] = next;
      saveFilterStore(store);
    },
    [scopeKey, enabled]
  );

  const savePreset = useCallback(
    (name: string) => {
      const preset: SavedFilterPreset = {
        id: `fp_${Date.now().toString(36)}`,
        name,
        state: filters as Record<string, unknown>,
        createdAt: Date.now(),
      };
      persistPresets([...presets, preset]);
    },
    [filters, presets, persistPresets]
  );

  const applyPreset = useCallback(
    (id: string) => {
      const preset = presets.find((p) => p.id === id);
      if (!preset) return;
      const restored = { ...defaultState, ...preset.state } as T;
      const cleaned = sanitize ? sanitize(restored) : restored;
      setFiltersRaw(cleaned);
      schedulePersist();
    },
    [presets, defaultState, sanitize, schedulePersist]
  );

  const deletePreset = useCallback(
    (id: string) => {
      persistPresets(presets.filter((p) => p.id !== id));
    },
    [presets, persistPresets]
  );

  // ── Multi-tab sync for presets via storage event ───────────────────────
  useEffect(() => {
    if (!enabled) return;
    const handler = (e: StorageEvent) => {
      if (e.key !== 'martha.filters') return;
      try {
        const store: FilterStore = e.newValue ? JSON.parse(e.newValue) : { presets: {} };
        setPresets(store.presets?.[scopeKey] || []);
      } catch {
        // ignore
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [scopeKey, enabled]);

  return {
    filters,
    setFilters,
    replaceFilters,
    clearFilters,
    hasActiveFilters,
    presets,
    savePreset,
    applyPreset,
    deletePreset,
  };
}
